/**
 * Barreira best-effort de apresentação.
 *
 * O objetivo é não deixar trabalho de stage two competir com o primeiro paint.
 * Nenhum sinal web prova que um pixel chegou à tela, então a barreira combina
 * quatro sinais e sempre termina: o pior caso é o watchdog.
 *
 * Limite conhecido e aceito: quando o documento é hospedado num iframe que o
 * pai esconde por CSS, `document.visibilityState` continua 'visible' e o FCP
 * não é emitido, mas `requestAnimationFrame` continua rodando. Nesse caso a
 * barreira abre pelo rAF, antes do reveal. Montar stage two ali não piora o
 * paint, que já está suprimido pelo container, e antecipa a tela final para o
 * instante em que a view aparece.
 *
 * Ordem dos sinais:
 *   1. PerformanceObserver de first-contentful-paint, com buffered: true
 *   2. visibilitychange, apenas quando o documento está de fato oculto
 *   3. double requestAnimationFrame, como heurística de agendamento
 *   4. watchdog, para a aplicação não travar esperando um sinal que não vem
 */

export type PresentationSignal = 'fcp' | 'visibility' | 'raf' | 'watchdog';

type Callback = (signal: PresentationSignal) => void;

const DEFAULT_WATCHDOG_MS = 2500;

let fired = false;
let firedWith: PresentationSignal | null = null;
let callbacks: Callback[] = [];
let armed = false;

export function presentationSignal(): PresentationSignal | null {
  return firedWith;
}

export function isPresented(): boolean {
  return fired;
}

function fire(signal: PresentationSignal): void {
  if (fired) return;
  fired = true;
  firedWith = signal;
  const pending = callbacks;
  callbacks = [];
  pending.forEach(cb => {
    try {
      cb(signal);
    } catch {
      // Um consumidor quebrado não pode impedir os outros de rodar.
    }
  });
}

function arm(watchdogMs: number): void {
  if (armed || typeof window === 'undefined') return;
  armed = true;

  // 1 — FCP. buffered pega o caso em que o paint já aconteceu antes deste
  // código rodar.
  try {
    const observer = new PerformanceObserver(list => {
      if (
        list.getEntries().some(entry => entry.name === 'first-contentful-paint')
      ) {
        observer.disconnect();
        fire('fcp');
      }
    });
    observer.observe({ type: 'paint', buffered: true });
  } catch {
    // WebKit sem PerformanceObserver de paint cai nos sinais seguintes.
  }

  // 2 — só faz sentido escutar se o documento começou oculto. Se ele começar
  // visível, este sinal nunca vai ser mais informativo que o FCP.
  if (document.visibilityState === 'hidden') {
    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.visibilityState === 'visible') fire('visibility');
      },
      true,
    );
  }

  // 3 — heurística de agendamento, não prova de paint.
  try {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => fire('raf'));
    });
  } catch {
    /* segue para o watchdog */
  }

  // 4 — a barreira sempre termina.
  setTimeout(() => fire('watchdog'), watchdogMs);
}

export function onAfterPresentation(
  callback: Callback,
  options: { watchdogMs?: number } = {},
): () => void {
  if (typeof window === 'undefined') return () => {};
  arm(options.watchdogMs ?? DEFAULT_WATCHDOG_MS);
  if (fired) {
    callback(firedWith as PresentationSignal);
    return () => {};
  }
  callbacks.push(callback);
  return () => {
    callbacks = callbacks.filter(cb => cb !== callback);
  };
}

/**
 * Rotas servidas dentro do shell nativo, onde o gate de apresentação do
 * container pode esconder o iframe até o `load`. Só nelas o carregamento de
 * terceiros é adiado, para não mudar o comportamento do resto do site.
 */
export const EMBED_PATH_PREFIXES = ['/api-payment/'];

export function isEmbedPath(pathname: string): boolean {
  return EMBED_PATH_PREFIXES.some(prefix => pathname.startsWith(prefix));
}
